import React from 'react';
import { Box, Card, Typography, Chip, Skeleton } from '@mui/material';
import { ArrowUpward, TrendingUp, InfoOutlined } from '@mui/icons-material';
import type { SimulatedImpact } from './simulatorUtils';

interface ImpactPreviewProps {
  impact: SimulatedImpact | null;
  isLoading?: boolean;
}

export const ImpactPreview: React.FC<ImpactPreviewProps> = ({ impact, isLoading = false }) => {
  const criticalDiff = impact ? impact.currentCritical - impact.projectedCritical : 0;
  const highDiff = impact ? impact.currentHigh - impact.projectedHigh : 0;
  const totalDiff = impact ? impact.currentTotal - impact.projectedTotal : 0;

  return (
    <Card
      elevation={0}
      sx={{
        bgcolor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        boxShadow: '0 4px 16px -2px rgba(15, 23, 42, 0.05)',
        p: { xs: 2.5, sm: 3 },
      }}
    >
      {/* Title */}
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <TrendingUp sx={{ fontSize: 20, color: '#2563eb' }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: '#0f172a',
              fontSize: '1.1rem',
              letterSpacing: '-0.01em',
            }}
          >
            Fix Impact Preview
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.84rem' }}>
          Estimated repository health movement based on the selected findings.
        </Typography>
      </Box>

      {/* Prominent Estimated Projected Health Banner */}
      <Box
        sx={{
          p: 2,
          mb: 2.5,
          borderRadius: '12px',
          bgcolor: '#f8fafc',
          border: '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: '#64748b',
            fontSize: '0.72rem',
          }}
        >
          Estimated projected health
        </Typography>

        {isLoading || !impact ? (
          <Box sx={{ py: 0.5 }}>
            <Skeleton variant="rounded" width={140} height={32} sx={{ borderRadius: '8px', bgcolor: '#f1f5f9' }} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '1.65rem',
                  fontWeight: 800,
                  color: '#0f172a',
                  lineHeight: 1,
                }}
              >
                {impact.currentHealth}
              </Typography>
              <Typography sx={{ color: '#94a3b8', fontSize: '1.2rem', fontWeight: 600 }}>
                →
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '1.65rem',
                  fontWeight: 800,
                  color: impact.healthDelta > 0 ? '#16a34a' : '#0f172a',
                  lineHeight: 1,
                }}
              >
                {impact.healthDelta > 0 ? `~${impact.projectedHealth}` : impact.currentHealth}
              </Typography>
            </Box>

            {impact.healthDelta > 0 && (
              <Chip
                icon={<ArrowUpward sx={{ fontSize: '13px !important' }} />}
                label={`+${impact.healthDelta}`}
                size="small"
                sx={{
                  bgcolor: '#dcfce7',
                  color: '#15803d',
                  fontWeight: 800,
                  fontSize: '0.75rem',
                  height: 22,
                  '& .MuiChip-icon': { color: '#15803d' },
                }}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Comparison Table */}
      <Box sx={{ mb: 2 }}>
        {/* Table Header */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1.5fr 1fr 1fr',
            pb: 1,
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#94a3b8',
              fontSize: '0.7rem',
            }}
          >
            Metric
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#94a3b8',
              fontSize: '0.7rem',
              textAlign: 'right',
            }}
          >
            Current
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#94a3b8',
              fontSize: '0.7rem',
              textAlign: 'right',
            }}
          >
            Projected
          </Typography>
        </Box>

        {/* Rows */}
        {/* 1. Health */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1.5fr 1fr 1fr',
            py: 1.25,
            borderBottom: '1px solid #f1f5f9',
            alignItems: 'center',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', fontSize: '0.85rem' }}>
            Health
          </Typography>
          {isLoading || !impact ? (
            <>
              <Skeleton width={35} sx={{ justifySelf: 'end' }} />
              <Skeleton width={45} sx={{ justifySelf: 'end' }} />
            </>
          ) : (
            <>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  color: '#64748b',
                  textAlign: 'right',
                }}
              >
                {impact.currentHealth}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                <Typography
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    color: impact.healthDelta > 0 ? '#16a34a' : '#0f172a',
                  }}
                >
                  {impact.healthDelta > 0 ? `~${impact.projectedHealth}` : impact.currentHealth}
                </Typography>
                {impact.healthDelta > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontWeight: 800,
                      fontSize: '0.72rem',
                      color: '#16a34a',
                    }}
                  >
                    ↑{impact.healthDelta}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Box>

        {/* 2. Critical Issues */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1.5fr 1fr 1fr',
            py: 1.25,
            borderBottom: '1px solid #f1f5f9',
            alignItems: 'center',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', fontSize: '0.85rem' }}>
            Critical
          </Typography>
          {isLoading || !impact ? (
            <>
              <Skeleton width={25} sx={{ justifySelf: 'end' }} />
              <Skeleton width={35} sx={{ justifySelf: 'end' }} />
            </>
          ) : (
            <>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  color: impact.currentCritical > 0 ? '#dc2626' : '#64748b',
                  textAlign: 'right',
                }}
              >
                {impact.currentCritical}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                <Typography
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    color: impact.projectedCritical === 0 && impact.currentCritical > 0 ? '#16a34a' : '#dc2626',
                  }}
                >
                  {impact.projectedCritical}
                </Typography>
                {criticalDiff > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontWeight: 800,
                      fontSize: '0.72rem',
                      color: '#16a34a',
                    }}
                  >
                    ↓{criticalDiff}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Box>

        {/* 3. High Issues */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1.5fr 1fr 1fr',
            py: 1.25,
            borderBottom: '1px solid #f1f5f9',
            alignItems: 'center',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', fontSize: '0.85rem' }}>
            High
          </Typography>
          {isLoading || !impact ? (
            <>
              <Skeleton width={25} sx={{ justifySelf: 'end' }} />
              <Skeleton width={35} sx={{ justifySelf: 'end' }} />
            </>
          ) : (
            <>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  color: impact.currentHigh > 0 ? '#ea580c' : '#64748b',
                  textAlign: 'right',
                }}
              >
                {impact.currentHigh}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                <Typography
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    color: impact.projectedHigh === 0 && impact.currentHigh > 0 ? '#16a34a' : '#ea580c',
                  }}
                >
                  {impact.projectedHigh}
                </Typography>
                {highDiff > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontWeight: 800,
                      fontSize: '0.72rem',
                      color: '#16a34a',
                    }}
                  >
                    ↓{highDiff}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Box>

        {/* 4. Total Issues */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1.5fr 1fr 1fr',
            py: 1.25,
            alignItems: 'center',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', fontSize: '0.85rem' }}>
            Total Issues
          </Typography>
          {isLoading || !impact ? (
            <>
              <Skeleton width={45} sx={{ justifySelf: 'end' }} />
              <Skeleton width={45} sx={{ justifySelf: 'end' }} />
            </>
          ) : (
            <>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  color: '#64748b',
                  textAlign: 'right',
                }}
              >
                {impact.currentTotal.toLocaleString()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                <Typography
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    color: '#0f172a',
                  }}
                >
                  {impact.projectedTotal.toLocaleString()}
                </Typography>
                {totalDiff > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontWeight: 800,
                      fontSize: '0.72rem',
                      color: '#16a34a',
                    }}
                  >
                    ↓{totalDiff}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Transparent Disclaimer */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, pt: 0.5 }}>
        <InfoOutlined sx={{ fontSize: 13, color: '#94a3b8', mt: 0.2, flexShrink: 0 }} />
        <Typography
          variant="caption"
          sx={{
            color: '#94a3b8',
            fontSize: '0.72rem',
            lineHeight: 1.35,
          }}
        >
          Projected health is an estimate based on severity weight of selected findings.
        </Typography>
      </Box>
    </Card>
  );
};

export default ImpactPreview;
